import React from "react"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { useNavigate } from "react-router-dom"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { ROUTES } from "constants/routes"
import { isConflict, useDummyData, useProjectHasFile } from "../api/useDummyData"

/** The Data Import tab of ProjectDetailsTabs. */
const DATA_IMPORT_TAB = PROJECT_TAB_KEYS.DATA_IMPORT

interface Props {
  projectId: string
  owner: string
  /** After dummy data was generated: read the project's data again. */
  onGenerated: () => void
}

/**
 * The action of a "no data yet" message, for a project with a questionnaire.
 * With a map, one click generates with the last run's profile (from the DQD's
 * provenance), or the defaults.
 * Without one it opens the Data Import method, so the naming is chosen there:
 * a default map could clash with the later XML export. A project that has
 * imported data after all is sent there too, where replacing it is asked.
 */
const DummyDataPrompt: React.FC<Props> = ({ projectId, owner, onGenerated }) => {
  const navigate = useNavigate()
  const { state, generate, isGenerating, isLoading } = useDummyData(projectId)
  const map = useProjectHasFile(projectId, owner, "map")
  const openMethod = (): void =>
    navigate(`${ROUTES.PROJECT(projectId)}?tab=${DATA_IMPORT_TAB}&method=dummy`)

  // Nothing until the map probe answers: the two buttons do different things.
  if (map.has === undefined) return null
  if (!map.has) {
    return (
      <DataQButton size="small" onClick={openMethod}>
        Set up dummy data
      </DataQButton>
    )
  }

  const run = async (): Promise<void> => {
    try {
      const res = await generate({ profile: state?.provenance?.profile ?? {} })
      notify.toast.success(`Generated ${res.report.completes} dummy respondents`)
      onGenerated()
    } catch (e) {
      if (isConflict(e)) {
        notify.toast.info(
          "This project already has imported data. Generate from Data Import to replace it."
        )
        openMethod()
        return
      }
      notify.error("Could not generate dummy data", e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <DataQButton
      size="small"
      isLoading={isGenerating}
      // Generating saves the profile it is given: never the defaults over a
      // saved one not read yet, or one that could not be read.
      isDisabled={isLoading || state === undefined}
      onClick={() => void run()}
    >
      Generate dummy data
    </DataQButton>
  )
}

export default DummyDataPrompt
